import Vue from "vue";
import { thumbPath, thumbUrl } from "./thumb";

/** 静态资源路径：legacy=后端根路径，unified=与 API 同在 /api 下 */
export type AssetMode = "legacy" | "unified";

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, "");
}

function isPrivateHost(host: string): boolean {
  if (!host) return true;
  if (host === "localhost" || host === "127.0.0.1") return true;
  if (host.startsWith("10.")) return true;
  if (host.startsWith("192.168.")) return true;
  const m = host.match(/^172\.(\d+)\./);
  if (m) {
    const n = Number(m[1]);
    if (n >= 16 && n <= 31) return true;
  }
  return false;
}

const DEFAULT_PUBLIC_BACKEND = "http://39.98.237.41:9094";
const DEFAULT_PRODUCTION_ORIGIN = "http://www.npu-aslp.org";

function isProductionAslpHost(host: string): boolean {
  return (
    host === "npu-aslp.org" ||
    host === "www.npu-aslp.org" ||
    host === "nwpu-aslp.org" ||
    host === "www.nwpu-aslp.org"
  );
}

function resolveServerAddr(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // 与当前页面同源，避免 HTTPS 页面请求 HTTP API 触发 mixed-content
    if (isProductionAslpHost(host)) {
      return window.location.origin;
    }
  }
  const fromEnv = process.env.VUE_APP_SERVER_ADDR as string | undefined;
  if (fromEnv && fromEnv.trim()) {
    return trimTrailingSlash(fromEnv.trim());
  }
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (isProductionAslpHost(host)) {
      return DEFAULT_PRODUCTION_ORIGIN;
    }
    if (isPrivateHost(host)) {
      return DEFAULT_PUBLIC_BACKEND;
    }
    return `${window.location.protocol}//${window.location.hostname}:9094`;
  }
  return "http://localhost:9094";
}

function resolveAssetMode(): AssetMode {
  const mode = (process.env.VUE_APP_ASSET_MODE as string | undefined)?.toLowerCase();
  if (mode === "legacy" || mode === "unified") {
    return mode;
  }
  const addr = resolveServerAddr();
  if (addr.includes("npu-aslp.org") || addr.includes("nwpu-aslp.org")) {
    return "unified";
  }
  return "legacy";
}

export const serverAddr = resolveServerAddr();
export const assetMode = resolveAssetMode();

/** POST/GET 接口：{origin}/api/{path} */
export function apiUrl(path: string): string {
  const p = path.replace(/^\/+/, "").replace(/^api\//, "");
  return `${serverAddr}/api/${p}`;
}

/** 图片等静态资源 */
export function assetUrl(relativePath: string): string {
  const p = relativePath.replace(/^\/+/, "");
  if (!p) return serverAddr;
  if (assetMode === "unified") {
    return `${serverAddr}/api/${p}`;
  }
  return `${serverAddr}/${p}`;
}

/** 替换 Markdown/HTML 中的 localhost 占位 */
export function replaceLocalhostInContent(content: string): string {
  const base =
    assetMode === "unified" ? `${serverAddr}/api` : serverAddr;
  return content.replace(/localhost/g, base);
}

/** 后台预览图片前缀（带末尾 /） */
export function backstageAssetBase(): string {
  return assetMode === "unified" ? `${serverAddr}/api/` : `${serverAddr}/`;
}

/** 后台插入图片时的 src 前缀 */
export function picStartSnippet(): string {
  if (assetMode === "unified") {
    return `<img src="${serverAddr}/api/`;
  }
  return '<img src="localhost/';
}

export function installBackendConfig(vue: typeof Vue): void {
  vue.prototype.serverAddr = serverAddr;
  vue.prototype.assetMode = assetMode;
  vue.prototype.apiUrl = apiUrl;
  vue.prototype.assetUrl = assetUrl;
  vue.prototype.thumbPath = thumbPath;
  vue.prototype.thumbUrl = thumbUrl;
  vue.prototype.replaceLocalhostInContent = replaceLocalhostInContent;
  vue.prototype.backstageAssetBase = backstageAssetBase;
  vue.prototype.picStartSnippet = picStartSnippet;
}
